import React, { useState, useCallback, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ClipboardDocumentIcon, ClipboardDocumentCheckIcon } from '@heroicons/react/24/outline';
import { api } from '../../services/api';
import { useTranslation } from 'react-i18next';
import { FiRefreshCw } from 'react-icons/fi';

interface EmailGeneratorProps {
  onEmailGenerated: (email: string) => void;
}

export const EmailGenerator: React.FC<EmailGeneratorProps> = ({ onEmailGenerated }) => {
  const { t } = useTranslation();
  const [email, setEmail] = useState(() => {
    // 从 localStorage 获取保存的邮箱
    return localStorage.getItem('currentEmail') || '';
  });
  const [copied, setCopied] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [isGenerating, setIsGenerating] = useState(false);
  const COOLDOWN_TIME = 10; // 冷却时间（秒）

  // 保存邮箱到 localStorage
  useEffect(() => {
    if (email) {
      localStorage.setItem('currentEmail', email);
    }
  }, [email]);

  // 处理倒计时
  useEffect(() => {
    if (cooldown <= 0) return;

    const timer = setInterval(() => {
      setCooldown(prev => Math.max(0, prev - 1));
    }, 1000);

    return () => clearInterval(timer);
  }, [cooldown]);

  // 添加自动生成功能
  useEffect(() => {
    const autoGenerate = async () => {
      const savedEmail = localStorage.getItem('currentEmail');
      if (!savedEmail) {
        try {
          setIsGenerating(true);
          const response = await api.generateEmailAddress();
          const data = await response.json();
          setEmail(data.address);
          onEmailGenerated(data.address);
        } catch (error) {
          console.error('Failed to auto-generate email:', error);
        } finally {
          setIsGenerating(false);
        }
      }
    };

    autoGenerate();
  }, [onEmailGenerated]);

  const generateNewEmail = useCallback(async () => {
    if (cooldown > 0 || isGenerating) return;

    try {
      setIsGenerating(true);
      const response = await api.generateEmailAddress();
      const data = await response.json();
      setEmail(data.address);
      onEmailGenerated(data.address);
      setCopied(false);
      setCooldown(COOLDOWN_TIME);
    } catch (error) {
      console.error('Failed to generate email:', error);
    } finally {
      setIsGenerating(false);
    }
  }, [cooldown, onEmailGenerated, isGenerating]);

  const copyToClipboard = async () => {
    if (!email) return;
    
    try {
      await navigator.clipboard.writeText(email);
      setCopied(true);
      
      setTimeout(() => {
        setCopied(false);
      }, 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="max-w-xl mx-auto px-4 w-full"
    >
      <div className="backdrop-blur-md bg-white/70 rounded-2xl p-6">  
        <div className="flex flex-col sm:flex-row gap-4 items-center">
          {/* 邮箱显示区域 */}
          <div className="flex-1 w-full">
            <div className={`
              bg-gray-50 rounded-xl p-4 
              flex items-center justify-between 
              min-w-[280px]
              border-2 
              ${email ? 'border-primary-light/30 shadow-sm shadow-primary-light/20' : 'border-transparent'}
              transition-all duration-300
            `}>
              <div className="flex-1 flex justify-center">
                <span className={`
                  text-sm sm:text-base 
                  ${email ? 'text-gray-900' : 'text-gray-400'}
                  max-w-[400px] truncate
                `}>
                  {email || t('emailGenerator.placeholder')}
                </span>
              </div>
              {email && (
                <button
                  onClick={copyToClipboard}
                  className="flex-shrink-0 ml-2 text-gray-500 hover:text-primary-dark transition-colors"
                  title={t('emailGenerator.copyTooltip')}
                >
                  {copied ? (
                    <ClipboardDocumentCheckIcon className="w-5 h-5" />
                  ) : (
                    <ClipboardDocumentIcon className="w-5 h-5" />
                  )}
                </button>
              )}
            </div>
          </div>

          {/* 简化的生成按钮 */}
          <motion.button
            onClick={generateNewEmail}
            disabled={cooldown > 0 || isGenerating}
            className={`
              p-2 rounded-lg shadow-lg
              flex items-center justify-center
              transition-all
              relative
              flex-shrink-0
              ${(cooldown > 0 || isGenerating)
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-gradient-to-r from-primary-light to-primary hover:shadow-xl'
              }
            `}
            whileHover={(cooldown > 0 || isGenerating) ? {} : { scale: 1.05 }}
            whileTap={(cooldown > 0 || isGenerating) ? {} : { scale: 0.95 }}
            title={cooldown > 0 ? `${t('emailGenerator.cooldown')} ${cooldown}s` : t('emailGenerator.generateButton')}
          >
            <FiRefreshCw 
              className={`w-5 h-5 text-white ${isGenerating ? 'animate-spin' : ''}`}
            />
            
            {/* 冷却进度条 */}
            {cooldown > 0 && (
              <>
                {/* 冷却时间显示 */}
                <span className="absolute -top-6 left-1/2 transform -translate-x-1/2 text-xs text-gray-500 bg-white px-2 py-1 rounded-md shadow-sm">
                  {cooldown}s
                </span>
                
                {/* 环形进度条 */}
                <svg
                  className="absolute inset-0 w-full h-full -rotate-90"
                  viewBox="0 0 100 100"
                >
                  <circle
                    className="text-gray-200"
                    strokeWidth="8"
                    stroke="currentColor"
                    fill="transparent"
                    r="46"
                    cx="50"
                    cy="50"
                  />
                  <circle
                    className="text-primary-light transition-all duration-1000"
                    strokeWidth="8"
                    stroke="currentColor"
                    fill="transparent"
                    r="46"
                    cx="50"
                    cy="50"
                    strokeDasharray={`${(1 - cooldown / COOLDOWN_TIME) * 289} 289`}
                  />
                </svg>
              </>
            )}
          </motion.button>
        </div>

        <p className="text-xs text-gray-400 text-center mt-4">
          {t('emailGenerator.hint')}
        </p>
      </div>
    </motion.div>
  );
}; 